import React, { useContext, useMemo, useReducer, useState } from 'react';
import PageLayout from '../../pagelayout';
import { useIntl } from 'react-intl';
import translations from './praksisstedsok.i18n';
import { AdminApiContext } from '../../api/AdminApiContext';
import { HttpError } from '../../api/generated/admin-generated-api';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import { ChoosePraksisstedMatchingSearchDialog, SearchField } from './search';
import Praksisstedtree from './praksisstedtree';
import PraksisstedsokHelp from './praksisstedsokhelp';
import { SearchActionType, searchReducer } from './functions';
import { INIT_LIMIT, initSearchState, Search } from './types';
import { objectsAreEqual } from '../../util/objectUtils';
import usePrevValue from '../../hooks/usePrevValue';

const Praksisstedsok = () => {
  const { praksisstedApi } = useContext(AdminApiContext);
  const { addErrorNotification, addWarningNotification } = useContext(
    NotificationFunctionContext,
  );

  const [
    {
      search,
      searchResults,
      searchResultsModalOpen,
      chosenPlace: chosenPraksisstedId,
    },
    dispatchSearchResultUpdate,
  ] = useReducer(searchReducer, initSearchState);
  const prevSearch = usePrevValue(search);

  const [loading, setLoading] = useState<boolean>(false);

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      title: formatMessage({ id: translations.title.id }),
      subtitle: formatMessage({ id: translations.subtitle.id }),
      limitHitWarning: formatMessage({ id: translations.limitHitWarning.id }),
    }),
    [formatMessage],
  );

  const sendSearchRequest = () => {
    if (
      chosenPraksisstedId &&
      prevSearch &&
      objectsAreEqual(search, prevSearch)
    ) {
      dispatchSearchResultUpdate({
        type: SearchActionType.SET_OPEN,
        payload: {
          searchResultsModalOpen: true,
        },
      });

      return;
    }

    setLoading(true);

    const { searchString, limit, includeDeleted } = search;

    praksisstedApi
      .praksisstederFulltekstGet({
        queryParams: { fulltekst: searchString, limit, includeDeleted },
      })
      .then((res) => {
        dispatchSearchResultUpdate({
          type: SearchActionType.SET_RESULTS,
          payload: {
            search,
            searchResults: res,
          },
        });

        if (res.length >= (limit || INIT_LIMIT)) {
          addWarningNotification(labels.limitHitWarning);
        }
      })
      .catch((err: HttpError) => addErrorNotification(err.message))
      .finally(() => setLoading(false));
  };

  const onSearchResultModalClose = (
    confirm: boolean,
    praksisstedId?: string,
  ) => {
    if (confirm && praksisstedId) {
      dispatchSearchResultUpdate({
        type: SearchActionType.SET_CHOSEN_STED,
        payload: {
          chosenPlace: praksisstedId,
          searchResultsModalOpen: false,
        },
      });
    } else {
      dispatchSearchResultUpdate({
        type: SearchActionType.SET_OPEN,
        payload: {
          searchResultsModalOpen: false,
        },
      });
    }
  };

  const setSearch = (newSearch: Search) =>
    dispatchSearchResultUpdate({
      type: SearchActionType.SET_SEARCH,
      payload: { search: newSearch },
    });

  return (
    <PageLayout isIndented title={labels.title} subtitle={labels.subtitle}>
      <SearchField
        onSearch={sendSearchRequest}
        loading={loading}
        search={search}
        setSearch={setSearch}
      />
      <ChoosePraksisstedMatchingSearchDialog
        praksisstederMatchingSearch={searchResults}
        onClose={onSearchResultModalClose}
        isOpen={searchResultsModalOpen}
      />
      {chosenPraksisstedId && (
        <Praksisstedtree
          chosenPraksisstedId={chosenPraksisstedId}
          includeDeleted={search.includeDeleted}
        />
      )}
      <PraksisstedsokHelp />
    </PageLayout>
  );
};

export default Praksisstedsok;
